import { CheckCircle2, Circle, ChevronRight, X, UserPlus, HardHat } from 'lucide-react';
import { useEmployeeSetup } from '@/contexts/EmployeeSetupContext';

const STEPS = [
  { label: 'Crear perfil' },
  { label: 'Asignar credencial' },
  { label: 'Asignar grupo de acceso' },
];

const EmployeeSetupWizard: React.FC = () => {
  const { isActive, currentStep, completedSteps, employee, employeeType, cancelSetup } = useEmployeeSetup();

  if (!isActive) return null;

  const isCompleted = completedSteps.includes(3);
  const employeeName = employee
    ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || employee.name || ''
    : '';

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-4 pointer-events-none">
      <div className="bg-slate-900 text-white rounded-2xl shadow-2xl border border-slate-700 pointer-events-auto overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-slate-800 border-b border-slate-700">
          <div className="flex items-center gap-2.5">
            {employeeType === 'contractor'
              ? <HardHat size={16} className="text-amber-400" />
              : <UserPlus size={16} className="text-brand" />
            }
            <span className="text-sm font-semibold text-slate-100">
              {isCompleted
                ? '¡Configuración completada!'
                : employeeName
                  ? `Configurando: ${employeeName}`
                  : `Nuevo ${employeeType === 'contractor' ? 'contratista' : 'empleado'}`
              }
            </span>
          </div>
          {!isCompleted && (
            <button
              onClick={cancelSetup}
              className="text-slate-400 hover:text-white transition-colors p-1 rounded"
              title="Cancelar configuración"
            >
              <X size={15} />
            </button>
          )}
        </div>

        {/* Steps */}
        <div className="flex items-center px-5 py-4 gap-2">
          {STEPS.map((step, idx) => {
            const stepNum = idx + 1;
            const done = completedSteps.includes(stepNum);
            const active = currentStep === stepNum && !isCompleted;
            const pending = !done && !active;

            return (
              <div key={stepNum} className="flex items-center gap-2 flex-1 min-w-0">
                {/* Step indicator */}
                <div className={`flex items-center gap-2 min-w-0 ${pending ? 'opacity-40' : ''}`}>
                  <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                    done
                      ? 'bg-emerald-500'
                      : active
                        ? 'bg-brand ring-2 ring-brand/40'
                        : 'bg-slate-700'
                  }`}>
                    {done
                      ? <CheckCircle2 size={15} className="text-white" />
                      : active
                        ? <span className="text-white text-xs font-bold">{stepNum}</span>
                        : <Circle size={13} className="text-slate-500" />
                    }
                  </div>
                  <span className={`text-xs font-medium truncate ${
                    done ? 'text-emerald-400' : active ? 'text-white' : 'text-slate-500'
                  }`}>
                    {step.label}
                  </span>
                </div>

                {/* Connector */}
                {idx < STEPS.length - 1 && (
                  <ChevronRight size={14} className={`flex-shrink-0 ${done ? 'text-emerald-500' : 'text-slate-600'}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Tip */}
        {!isCompleted && (
          <div className="px-5 pb-3">
            <p className="text-xs text-slate-400">
              {currentStep === 1 && 'Completa el formulario y haz clic en "Crear" para continuar.'}
              {currentStep === 2 && 'El empleado ya está preseleccionado. Elige el tipo de credencial y asígnala.'}
              {currentStep === 3 && 'Selecciona un grupo de acceso de la lista para agregar al empleado.'}
            </p>
          </div>
        )}

        {/* Completion bar */}
        {isCompleted && (
          <div className="h-1 bg-emerald-500 w-full" />
        )}
      </div>
    </div>
  );
};

export default EmployeeSetupWizard;
